import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { ListLayoutColumn } from '../models/list-layout-column';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ListLayoutColumnsService extends CrudService implements BatchEditableTableServiceInterface<ListLayoutColumn> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    /** Liste les colonnes */
    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<ListLayoutColumn>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${context.value}/columns`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<ListLayoutColumn>(ListLayoutColumn, x);
                console.log(res);
                return res;
            })
        );
    }

    /** Export les colonnes */
    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos) {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);
        return this.http
            .get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${context.value}/columns/export`, {
                headers: headers,
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    /** Mise à jour d'une colonne */
    update(listLayoutId: number, listLayoutColumnId: number, listLayoutColumn: ListLayoutColumn): Observable<ListLayoutColumn> {
        return this.http
            .put<ListLayoutColumn>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${listLayoutId}/columns/${listLayoutColumnId}`,
                listLayoutColumn,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /** Supression d'une colonne */
    delete(listLayoutId: number, listLayoutColumnId: number): Observable<boolean> {
        return this.http
            .delete<ListLayoutColumn>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${listLayoutId}/columns/${listLayoutColumnId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    /** Ajout d'une colonne */
    add(listLayoutId: number, listLayoutColumn: ListLayoutColumn): Observable<ListLayoutColumn> {
        return this.http
            .post<ListLayoutColumn>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${listLayoutId}/columns`,
                listLayoutColumn,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /** Recupération des informations d'une colonne */
    get(listLayoutId: number, listLayoutColumnId: number): Observable<ListLayoutColumn> {
        return this.http
            .get<ListLayoutColumn>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${listLayoutId}/columns/${listLayoutColumnId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    applyOrderChanges(listLayoutId: number, listLayoutColumns: ListLayoutColumn[]) {
        // Appel de la Web Api
        return this.http
            .patch<ListLayoutColumn[]>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${listLayoutId}/columns`,
                listLayoutColumns,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<ListLayoutColumn>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        let listLayoutId = context.value as number;

        modifications.deletes.forEach((x) => {
            requests.push(this.delete(listLayoutId, x.data.id as number).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(listLayoutId, x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            x.data.attributeId = +x.data.attributeId;
            requests.push(this.add(listLayoutId, x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }
}
